import React, { createContext, useContext, useState, useCallback, useEffect } from "react"
import { Toast, ToastContainer } from "react-bootstrap"

const DEFAULT_TOAST_DURATION = 5000

type ToastMessage = {
  id: string
  title: string
  text: string
  timestamp: number
}

type ToastContextType = object
const ToastContext = createContext<ToastContextType>({})

let toastRef: (title: string, text: string, unique: boolean) => void = () => {}

export const showToastMessage = (title: string, text: string, unique = false) => {
  if (toastRef) {
    toastRef(title, text, unique)
  } else {
    console.log("Toast system not ready yet")
  }
}

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastMessage[]>([])

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id))
  }, [])

  const addToast = useCallback(
    (title: string, text: string, unique = false) => {
      setToasts((prev) => {
        // If unique is true, check if there's already a toast with the same title and text
        if (unique && prev.some((toast) => toast.title === title && toast.text === text)) {
          // If a toast with the same content exists, don't add a new one
          return prev
        }

        const id = `toast-${Date.now()}-${Math.random().toString().slice(2, 8)}`
        const newToast: ToastMessage = {
          id,
          title,
          text,
          timestamp: Date.now(),
        }

        // Schedule the removal of the toast
        setTimeout(() => {
          removeToast(id)
        }, DEFAULT_TOAST_DURATION)

        return [...prev, newToast]
      })
    },
    [removeToast],
  )

  useEffect(() => {
    toastRef = addToast
  }, [addToast])

  const contextValue = {
    // use showToastMessage to show a toast from anywhere
  }

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <ToastContainer position="bottom-start" className="p-3" style={{ zIndex: 9999 }}>
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            show
            onClose={() => removeToast(toast.id)}
            delay={DEFAULT_TOAST_DURATION}
            autohide
            bg="light"
          >
            <Toast.Header closeButton>
              <strong className="me-auto">{toast.title}</strong>
              <small className="text-muted">{new Date(toast.timestamp).toLocaleTimeString()}</small>
            </Toast.Header>
            <Toast.Body>{toast.text}</Toast.Body>
          </Toast>
        ))}
      </ToastContainer>
    </ToastContext.Provider>
  )
}

export const useToast = () => useContext(ToastContext)
